// Saving annotations: an incremental update appended to the original bytes (ISO 32000-1 §7.5.6) with new annotation objects and their
// appearance streams, so any reader draws them the way this one does.

import { PdfDocument } from "./document";
import { Dict, PName, PRef, Stream, type PObj } from "./parse";
import { writeFormValues, writeFieldEdits, type FieldEdit } from "./form";
import { writeFieldWidgets, type FieldAnnot } from "./formdesign";
import { embedFonts, type EmbedRequest, type EmbeddedFont } from "./embed";
import { TrueTypeFont } from "./truetype";

export type Rect = [number, number, number, number];

/** Annotations the Reader adds. Coordinates are PDF user space (points, origin bottom-left). */
export type UserAnnot =
    | { id: string; kind: "freetext"; page: number; rect: Rect; lines: string[]; fontSize: number; color: string; bold: boolean; family: string; fill?: string | null; subject?: string; align?: "left" | "center" | "right"; spacing?: number; lineWidths?: number[]; italic?: boolean; font?: { key: string; sfnt: Uint8Array }; /** Faces behind `font` (or behind the standard font) that draw the characters it lacks, in order. */ fallbacks?: Array<{ key: string; family: string; sfnt: Uint8Array }>; /** The look of the text this box replaces, so "Match original" can bring it back. Not written to the file. */ orig?: { baseFont: string; family: string; bold: boolean; italic: boolean; size: number; color: string }; /** The colour the edit box is painted in while typing (the page's own background, or a contrasting neutral). Not written. */ surface?: string }
    | { id: string; kind: "square" | "circle"; page: number; rect: Rect; stroke: string | null; fill: string | null; width: number; subject?: string }
    | { id: string; kind: "line" | "arrow"; page: number; p1: [number, number]; p2: [number, number]; stroke: string; width: number; subject?: string }
    | { id: string; kind: "ink"; page: number; strokes: Array<Array<[number, number]>>; stroke: string; width: number; subject?: string }
    | { id: string; kind: "stamp"; page: number; rect: Rect; image: { bytes: Uint8Array; mime: string; width: number; height: number }; name: string; subject?: string; /** 0..1, 1 = solid */ opacity?: number; /** degrees, clockwise, about the middle of `rect` */ rotation?: number }
    | { id: string; kind: "note"; page: number; rect: Rect; text: string; color?: string; subject?: string }
    | FieldAnnot
    | { id: string; kind: "redact"; page: number; rect: Rect; subject?: string }
    /** Recognised text (OCR): words with their boxes in user space, written as invisible text over the page picture. */
    | { id: string; kind: "ocr"; page: number; words: Array<{ t: string; box: Rect }>; subject?: string }
    | { id: string; kind: "highlight" | "underline" | "strikeout"; page: number; quads: number[][]; stroke: string; subject?: string };

const enc = new TextEncoder();
const num = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(4).replace(/0+$/, "").replace(/\.$/, ""));
const rgb = (hex: string): [number, number, number] => { const v = parseInt(hex.replace("#", "").slice(0, 6), 16) || 0; return [((v >> 16) & 255) / 255, ((v >> 8) & 255) / 255, (v & 255) / 255]; };
const rg = (hex: string, stroke: boolean) => { const [r, g, b] = rgb(hex); return `${num(r)} ${num(g)} ${num(b)} ${stroke ? "RG" : "rg"}`; };

export function pdfString(s: string): string {
    // WinAnsi bytes as an escaped literal string
    let out = "(";
    for (const ch of s) {
        const c = ch.codePointAt(0)!;
        const b = c < 256 ? c : winAnsi(c);
        if (b === 0x28 || b === 0x29 || b === 0x5c) out += "\\" + String.fromCharCode(b);
        else if (b < 32 || b > 126) out += "\\" + b.toString(8).padStart(3, "0");
        else out += String.fromCharCode(b);
    }
    return out + ")";
}
const WIN_EXTRA: Record<number, number> = { 0x20ac: 128, 0x201a: 130, 0x192: 131, 0x201e: 132, 0x2026: 133, 0x2020: 134, 0x2021: 135, 0x2c6: 136, 0x2030: 137, 0x160: 138, 0x2039: 139, 0x152: 140, 0x17d: 142, 0x2018: 145, 0x2019: 146, 0x201c: 147, 0x201d: 148, 0x2022: 149, 0x2013: 150, 0x2014: 151, 0x2dc: 152, 0x2122: 153, 0x161: 154, 0x203a: 155, 0x153: 156, 0x17e: 158, 0x178: 159 };
const winAnsi = (c: number) => WIN_EXTRA[c] ?? 63;

export function serialize(o: PObj | undefined, depth = 0): string {
    if (o === undefined || o === null) return "null";
    if (typeof o === "boolean") return o ? "true" : "false";
    if (typeof o === "number") return num(o);
    if (typeof o === "string") { let h = "<"; for (let i = 0; i < o.length; i++) h += o.charCodeAt(i).toString(16).padStart(2, "0"); return h + ">"; }
    if (o instanceof PName) return "/" + [...o.name].map((c) => { const k = c.charCodeAt(0); return k < 33 || k > 126 || "#()<>[]{}/%".includes(c) ? "#" + k.toString(16).padStart(2, "0") : c; }).join("");
    if (o instanceof PRef) return `${o.num} ${o.gen} R`;
    if (Array.isArray(o)) return "[" + o.map((x) => serialize(x, depth + 1)).join(" ") + "]";
    if (o instanceof Dict) { let s = "<<"; for (const k of o.keys()) s += serialize(new PName(k)) + " " + serialize(o.raw(k), depth + 1) + " "; return s + ">>"; }
    return "null";
}

export class Out {
    private parts: Uint8Array[] = [];
    length = 0;
    push(b: Uint8Array) { this.parts.push(b); this.length += b.length; }
    text(s: string) { this.push(enc.encode(s)); }
    bytes(): Uint8Array { const o = new Uint8Array(this.length); let p = 0; for (const x of this.parts) { o.set(x, p); p += x.length; } return o; }
}

export async function deflate(data: Uint8Array): Promise<Uint8Array | null> {
    try {
        const cs = new CompressionStream("deflate");
        const w = cs.writable.getWriter();
        void w.write(data as BufferSource);
        void w.close();
        return new Uint8Array(await new Response(cs.readable).arrayBuffer());
    } catch { return null; }
}

const FONTS: Record<string, string> = {
    Helvetica: "Helvetica", "Helvetica-Bold": "Helvetica-Bold", "Helvetica-Italic": "Helvetica-Oblique", "Helvetica-BoldItalic": "Helvetica-BoldOblique",
    Times: "Times-Roman", "Times-Bold": "Times-Bold", "Times-Italic": "Times-Italic", "Times-BoldItalic": "Times-BoldItalic",
    Courier: "Courier", "Courier-Bold": "Courier-Bold", "Courier-Italic": "Courier-Oblique", "Courier-BoldItalic": "Courier-BoldOblique"
};
function baseFont(family: string, bold: boolean, italic = false): string {
    const f = /times|serif/i.test(family) && !/sans/i.test(family) ? "Times" : /courier|mono/i.test(family) ? "Courier" : "Helvetica";
    return FONTS[f + (bold ? "-Bold" : "") + (italic ? (bold ? "Italic" : "-Italic") : "")] ?? "Helvetica";
}

/** Appends the cross-reference section (a table or a stream, matching the original) for the objects just written. */
export function writeXref(out: Out, offsets: Array<[number, number]>, nextNumStart: number, original: Uint8Array, xref: PdfDocument["xref"], root: PRef): void {
    const prev = (() => { const b = original; for (let i = b.length - 9; i >= Math.max(0, b.length - 2048); i--) { if (b[i] === 0x73 && b[i + 1] === 0x74 && b[i + 2] === 0x61 && b[i + 3] === 0x72 && b[i + 4] === 0x74 && b[i + 5] === 0x78) { let j = i + 9; let s = ""; while (j < b.length && (b[j]! === 32 || b[j]! === 10 || b[j]! === 13)) j++; while (j < b.length && b[j]! >= 48 && b[j]! <= 57) s += String.fromCharCode(b[j++]!); return parseInt(s, 10) || 0; } } return 0; })();
    const startAt = (() => { const t = new TextDecoder("latin1").decode(original.subarray(prev, prev + 8)); return t.startsWith("xref") ? "table" : "stream"; })();
    offsets.sort((a, b) => a[0] - b[0]);
    let nextNum = nextNumStart;
    const size = nextNum;
    const info = xref.trailer.raw("Info");
    const idArr = xref.trailer.raw("ID");
    const xrefOffset = out.length;
    if (startAt === "table") {
        let s = "xref\n";
        // consecutive runs
        let i = 0;
        while (i < offsets.length) {
            let j = i;
            while (j + 1 < offsets.length && offsets[j + 1]![0] === offsets[j]![0] + 1) j++;
            s += `${offsets[i]![0]} ${j - i + 1}\n`;
            for (let k = i; k <= j; k++) s += `${String(offsets[k]![1]).padStart(10, "0")} 00000 n \n`;
            i = j + 1;
        }
        s += `trailer\n<< /Size ${size} /Root ${root.num} ${root.gen} R ${info instanceof PRef ? `/Info ${info.num} ${info.gen} R ` : ""}${idArr ? `/ID ${serialize(idArr)} ` : ""}/Prev ${prev} >>\nstartxref\n${xrefOffset}\n%%EOF\n`;
        out.text(s);
    } else {
        const xnum = nextNum++;
        offsets.push([xnum, xrefOffset]);
        offsets.sort((a, b) => a[0] - b[0]);
        const rows: number[] = [];
        const index: number[] = [];
        let i = 0;
        while (i < offsets.length) {
            let j = i;
            while (j + 1 < offsets.length && offsets[j + 1]![0] === offsets[j]![0] + 1) j++;
            index.push(offsets[i]![0], j - i + 1);
            for (let k = i; k <= j; k++) { const o = offsets[k]![1]; rows.push(1, (o >>> 24) & 255, (o >>> 16) & 255, (o >>> 8) & 255, o & 255, 0); }
            i = j + 1;
        }
        const data = Uint8Array.from(rows);
        offsets.length = 0;
        out.text(`${xnum} 0 obj\n<< /Type /XRef /Size ${nextNum} /W [1 4 1] /Index [${index.join(" ")}] /Root ${root.num} ${root.gen} R ${info instanceof PRef ? `/Info ${info.num} ${info.gen} R ` : ""}${idArr ? `/ID ${serialize(idArr)} ` : ""}/Prev ${prev} /Length ${data.length} >>\nstream\n`);
        out.push(data);
        out.text(`\nendstream\nendobj\nstartxref\n${xrefOffset}\n%%EOF\n`);
    }
}

const WIN_OK = (cp: number) => cp < 0x80 || (cp >= 0xa0 && cp <= 0xff) || WIN_EXTRA[cp] !== undefined;

/** The characters of a text box that its own font (a standard one when none is embedded) has no glyph for. */
export function uncoveredChars(a: Extract<UserAnnot, { kind: "freetext" }>): string {
    let prog: TrueTypeFont | null = null;
    try { prog = a.font ? new TrueTypeFont(a.font.sfnt) : null; } catch { prog = null; }
    const miss = new Set<string>();
    for (const ln of a.lines) for (const ch of ln) { const cp = ch.codePointAt(0)!; if (cp <= 32) continue; const ok = a.font ? (prog?.gidByUnicode(cp) ?? 0) > 0 : WIN_OK(cp); if (!ok) miss.add(ch); }
    return [...miss].join("");
}

/** Which face draws each character of a text box: face 0 is the box's own font (a standard one when none is embedded), then its fallbacks. */
export function planFaces(a: Extract<UserAnnot, { kind: "freetext" }>): { faces: Array<{ key: string; family: string; sfnt: Uint8Array } | null>; runs: Array<Array<{ face: number; text: string }>> } {
    const faces: Array<{ key: string; family: string; sfnt: Uint8Array } | null> = [a.font ? { key: a.font.key, family: a.family, sfnt: a.font.sfnt } : null, ...(a.fallbacks ?? [])];
    const progs = faces.map((f) => { try { return f ? new TrueTypeFont(f.sfnt) : null; } catch { return null; } });
    const covers = (i: number, cp: number) => (faces[i] ? (progs[i]?.gidByUnicode(cp) ?? 0) > 0 : WIN_OK(cp));
    const runs = a.lines.map((ln) => {
        const out: Array<{ face: number; text: string }> = [];
        for (const ch of ln) {
            const cp = ch.codePointAt(0)!;
            let i = 0;
            if (!covers(0, cp)) { const j = faces.findIndex((_, k) => k > 0 && covers(k, cp)); if (j > 0) i = j; }
            const last = out[out.length - 1];
            if (last && last.face === i) last.text += ch; else out.push({ face: i, text: ch });
        }
        return out;
    });
    return { faces, runs };
}

/** Writes the document's bytes with `annots` added. Throws for encrypted files. */
/** `forms` carries filled-in form fields: field name → value (text, option value, or "Off" / the on-state name). */
export async function saveWithAnnotations(original: Uint8Array, annots: UserAnnot[], forms?: Map<string, string>, fieldEdits?: Map<number, FieldEdit>): Promise<Uint8Array> {
    // redaction marks are applied by redactPages, never saved as annotations
    annots = annots.filter((a) => a.kind !== "redact");
    if (!annots.length && !forms?.size && !fieldEdits?.size) return original;
    const doc = PdfDocument.open(original);
    if (doc.xref.trailer.has("Encrypt")) throw new Error("This PDF is encrypted, so annotations can't be saved into it.");
    const xref = doc.xref;
    const pages = doc.pages();
    const root = xref.trailer.raw("Root");
    if (!(root instanceof PRef)) throw new Error("The PDF has no catalog reference.");
    let nextNum = Math.max(xref.trailer.num("Size"), ...[...xref.entries.keys()].map((k) => k + 1));
    const out = new Out();
    out.push(original);
    if (original[original.length - 1] !== 10) out.text("\n");
    const offsets: Array<[number, number]> = [];
    const writeObj = (num: number, body: string | Uint8Array, gen = 0) => {
        offsets.push([num, out.length]);
        out.text(`${num} ${gen} obj\n`);
        if (typeof body === "string") out.text(body); else out.push(body);
        out.text("\nendobj\n");
    };
    const newRef = () => nextNum++;
    const writeStream = async (num: number, dict: string, data: Uint8Array, compress = true) => {
        let payload = data, filter = "";
        if (compress && data.length > 80) { const z = await deflate(data); if (z && z.length < data.length) { payload = z; filter = "/Filter /FlateDecode "; } }
        offsets.push([num, out.length]);
        out.text(`${num} 0 obj\n<< ${dict} ${filter}/Length ${payload.length} >>\nstream\n`);
        out.push(payload);
        out.text("\nendstream\nendobj\n");
    };

    // fonts the people picked beyond the standard ones: subset and embedded once per font
    const wanted = new Map<string, EmbedRequest>();
    const plans = new Map<string, ReturnType<typeof planFaces>>();
    for (const a of annots) {
        if (a.kind !== "freetext" || (!a.font && !a.fallbacks?.length)) continue;
        const plan = planFaces(a);
        plans.set(a.id, plan);
        plan.runs.forEach((line) => line.forEach((run) => {
            const f = plan.faces[run.face];
            if (!f) return;
            const r = wanted.get(f.key) ?? { key: f.key, family: f.family, italic: Boolean(a.italic), sfnt: f.sfnt, texts: [] };
            r.texts.push(run.text);
            wanted.set(f.key, r);
        }));
    }
    const embedded: Map<string, EmbeddedFont> = wanted.size ? await embedFonts([...wanted.values()], { newRef, writeObj: (n, b) => writeObj(n, b), writeStream: (n, d, data, c) => writeStream(n, d, data, c) }) : new Map();

    const byPage = new Map<number, UserAnnot[]>();
    for (const a of annots) { const l = byPage.get(a.page - 1) ?? []; l.push(a); byPage.set(a.page - 1, l); }
    const date = (() => { const d = new Date(); const p = (n: number) => String(n).padStart(2, "0"); return `D:${d.getUTCFullYear()}${p(d.getUTCMonth() + 1)}${p(d.getUTCDate())}${p(d.getUTCHours())}${p(d.getUTCMinutes())}${p(d.getUTCSeconds())}Z`; })();

    const topFields: PRef[] = [];
    for (const [pi, list] of byPage) {
        const page = pages[pi];
        const pref = page?.ref;
        if (!page || !pref) continue;
        const newRefs: PRef[] = [];
        const fieldList = list.filter((a): a is FieldAnnot => a.kind === "field" && a.ftype !== "image");
        if (fieldList.length) {
            const made = await writeFieldWidgets(fieldList, pref, { newRef, writeObj: (n, b) => writeObj(n, b), writeStream: (n, d, data, c) => writeStream(n, d, data, c) });
            newRefs.push(...made.widgets);
            topFields.push(...made.top);
        }
        for (const a of list) {
            if (a.kind === "field" || a.kind === "redact" || a.kind === "ocr") continue; // fields are written above; image fields later (applyExtras)
            const annotNum = newRef();
            const apNum = newRef();
            const common = `/Type /Annot /P ${pref.num} ${pref.gen} R /F 4 /M ${pdfString(date)} /NM ${pdfString(a.id)} ${a.subject ? `/Subj ${pdfString(a.subject)} ` : ""}`;
            let rect: Rect;
            let content = "";
            let resources = "";
            let subtype: string;
            let extra = "";
            let imageNum = 0, maskNum = 0;
            switch (a.kind) {
                case "freetext": {
                    subtype = "FreeText"; rect = a.rect;
                    const [x0, y0, x1, y1] = rect;
                    const plan = plans.get(a.id);
                    const emb = a.font ? embedded.get(a.font.key) : undefined;
                    const font = baseFont(a.family, a.bold, a.italic);
                    const stdFont = `<< /Type /Font /Subtype /Type1 /BaseFont /${font} /Encoding /WinAnsiEncoding >>`;
                    // font resources: /F1 is the box's own font; /F2.. are the fallback faces that drew characters it lacks
                    const faceRes = plan ? plan.faces.map((f, i) => (i === 0 ? (emb ? `/F1 ${emb.ref} 0 R` : `/F1 ${stdFont}`) : f && embedded.get(f.key) ? `/F${i + 1} ${embedded.get(f.key)!.ref} 0 R` : "")).join(" ") : "";
                    resources = plan ? `/Font << ${faceRes} >>` : emb ? `/Font << /F1 ${emb.ref} 0 R >>` : `/Font << /F1 ${stdFont} >>`;
                    if (a.fill) content += `${rg(a.fill, false)} 0 0 ${num(x1 - x0)} ${num(y1 - y0)} re f\n`;
                    content += `BT /F1 ${num(a.fontSize)} Tf ${rg(a.color, false)} `;
                    const step = a.fontSize * (a.spacing ?? 1.2);
                    let y = y1 - y0 - a.fontSize * 0.95 - 2;
                    a.lines.forEach((ln, i) => {
                        const lw = a.lineWidths?.[i] ?? 0;
                        const x = a.align === "center" ? Math.max(2, (x1 - x0 - lw) / 2) : a.align === "right" ? Math.max(2, x1 - x0 - lw - 2) : 2;
                        if (plan) {
                            content += `1 0 0 1 ${num(x)} ${num(y)} Tm `;
                            for (const run of plan.runs[i] ?? []) {
                                const face = plan.faces[run.face];
                                const e = run.face === 0 ? emb : face ? embedded.get(face.key) : undefined;
                                content += `/F${run.face + 1} ${num(a.fontSize)} Tf ${e ? e.hex(run.text) : pdfString(run.text)} Tj `;
                            }
                            content += "\n";
                        } else content += `1 0 0 1 ${num(x)} ${num(y)} Tm ${emb ? emb.hex(ln) : pdfString(ln)} Tj\n`;
                        y -= step;
                    });
                    content += "ET\n";
                    extra = `/Contents ${pdfString(a.lines.join("\r"))} /DA (/F1 ${num(a.fontSize)} Tf ${rg(a.color, false)}) /DS (font: ${font} ${num(a.fontSize)}pt) /Q ${a.align === "center" ? 1 : a.align === "right" ? 2 : 0} `;
                    break;
                }
                case "note": {
                    subtype = "Text"; rect = a.rect;
                    const [x0, y0, x1, y1] = rect, w = x1 - x0, h = y1 - y0, c = a.color ?? "#ffd23f";
                    resources = "";
                    content += `${rg(c, false)} 0 0 ${num(w)} ${num(h)} re f\n0.45 0.35 0 RG 1 w 0.5 0.5 ${num(w - 1)} ${num(h - 1)} re S\n`;
                    for (const f of [0.68, 0.48, 0.28]) content += `0.45 0.35 0 RG 0.9 w ${num(w * 0.2)} ${num(h * f)} m ${num(w * (f > 0.4 ? 0.8 : 0.55))} ${num(h * f)} l S\n`;
                    extra = `/Contents ${pdfString(a.text)} /Name /Comment /Open false /C [${rgb(c).map(num).join(" ")}] /Subj (Note) `;
                    break;
                }
                case "square": case "circle": {
                    subtype = a.kind === "square" ? "Square" : "Circle"; rect = a.rect;
                    const [x0, y0, x1, y1] = rect, w = x1 - x0, h = y1 - y0, hw = a.width / 2;
                    if (a.fill) content += rg(a.fill, false) + "\n";
                    if (a.stroke) content += `${rg(a.stroke, true)} ${num(a.width)} w\n`;
                    if (a.kind === "square") content += `${num(hw)} ${num(hw)} ${num(w - a.width)} ${num(h - a.width)} re\n`;
                    else { const rx = (w - a.width) / 2, ry = (h - a.width) / 2, cx = w / 2, cy = h / 2, k = 0.5522847498; content += `${num(cx + rx)} ${num(cy)} m ${num(cx + rx)} ${num(cy + k * ry)} ${num(cx + k * rx)} ${num(cy + ry)} ${num(cx)} ${num(cy + ry)} c ${num(cx - k * rx)} ${num(cy + ry)} ${num(cx - rx)} ${num(cy + k * ry)} ${num(cx - rx)} ${num(cy)} c ${num(cx - rx)} ${num(cy - k * ry)} ${num(cx - k * rx)} ${num(cy - ry)} ${num(cx)} ${num(cy - ry)} c ${num(cx + k * rx)} ${num(cy - ry)} ${num(cx + rx)} ${num(cy - k * ry)} ${num(cx + rx)} ${num(cy)} c h\n`; }
                    content += a.fill && a.stroke ? "B\n" : a.fill ? "f\n" : a.stroke ? "S\n" : "n\n";
                    extra = `${a.stroke ? `/C [${rgb(a.stroke).map(num).join(" ")}] ` : ""}${a.fill ? `/IC [${rgb(a.fill).map(num).join(" ")}] ` : ""}/BS << /W ${num(a.width)} >> `;
                    break;
                }
                case "line": case "arrow": {
                    subtype = "Line";
                    const pad = a.width * 4 + 2;
                    rect = [Math.min(a.p1[0], a.p2[0]) - pad, Math.min(a.p1[1], a.p2[1]) - pad, Math.max(a.p1[0], a.p2[0]) + pad, Math.max(a.p1[1], a.p2[1]) + pad];
                    const [ox, oy] = [rect[0], rect[1]];
                    const [x1, y1, x2, y2] = [a.p1[0] - ox, a.p1[1] - oy, a.p2[0] - ox, a.p2[1] - oy];
                    content += `${rg(a.stroke, true)} ${num(a.width)} w 1 J ${num(x1)} ${num(y1)} m ${num(x2)} ${num(y2)} l S\n`;
                    if (a.kind === "arrow") {
                        const ang = Math.atan2(y2 - y1, x2 - x1), len = a.width * 4 + 4;
                        content += `${rg(a.stroke, false)} ${num(x2)} ${num(y2)} m ${num(x2 - len * Math.cos(ang - 0.45))} ${num(y2 - len * Math.sin(ang - 0.45))} l ${num(x2 - len * Math.cos(ang + 0.45))} ${num(y2 - len * Math.sin(ang + 0.45))} l h f\n`;
                    }
                    extra = `/L [${[a.p1[0], a.p1[1], a.p2[0], a.p2[1]].map(num).join(" ")}] /C [${rgb(a.stroke).map(num).join(" ")}] /BS << /W ${num(a.width)} >> ${a.kind === "arrow" ? "/LE [/None /OpenArrow] " : ""}`;
                    break;
                }
                case "ink": {
                    subtype = "Ink";
                    const pts = a.strokes.flat();
                    const pad = a.width + 1;
                    rect = [Math.min(...pts.map((p) => p[0])) - pad, Math.min(...pts.map((p) => p[1])) - pad, Math.max(...pts.map((p) => p[0])) + pad, Math.max(...pts.map((p) => p[1])) + pad];
                    content += `${rg(a.stroke, true)} ${num(a.width)} w 1 J 1 j\n`;
                    for (const s of a.strokes) { s.forEach((p, i) => { content += `${num(p[0] - rect[0])} ${num(p[1] - rect[1])} ${i ? "l" : "m"} `; }); content += "S\n"; }
                    extra = `/InkList [${a.strokes.map((s) => "[" + s.map((p) => `${num(p[0])} ${num(p[1])}`).join(" ") + "]").join(" ")}] /C [${rgb(a.stroke).map(num).join(" ")}] /BS << /W ${num(a.width)} >> `;
                    break;
                }
                case "stamp": {
                    subtype = "Stamp"; rect = a.rect;
                    imageNum = newRef();
                    const w = a.image.width, h = a.image.height;
                    if (/jpe?g/.test(a.image.mime)) await writeStream(imageNum, `/Type /XObject /Subtype /Image /Width ${w} /Height ${h} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode`, a.image.bytes, false);
                    else {
                        const { rgb: pix, alpha } = await rasterize(a.image.bytes, a.image.mime, w, h);
                        if (alpha) { maskNum = newRef(); await writeStream(maskNum, `/Type /XObject /Subtype /Image /Width ${w} /Height ${h} /ColorSpace /DeviceGray /BitsPerComponent 8`, alpha); }
                        await writeStream(imageNum, `/Type /XObject /Subtype /Image /Width ${w} /Height ${h} /ColorSpace /DeviceRGB /BitsPerComponent 8 ${maskNum ? `/SMask ${maskNum} 0 R` : ""}`, pix);
                    }
                    resources = `/XObject << /Im1 ${imageNum} 0 R >>`;
                    // see-through and turned pictures: the transparency goes in the appearance, the turn in its matrix, and the
                    // annotation's /Rect becomes the box that holds the turned picture
                    const op = a.opacity === undefined ? 1 : Math.min(1, Math.max(0, a.opacity));
                    if (op < 1) resources += ` /ExtGState << /GS << /Type /ExtGState /ca ${num(op)} /CA ${num(op)} >> >>`;
                    const w0 = a.rect[2] - a.rect[0], h0 = a.rect[3] - a.rect[1];
                    const th = -((a.rotation ?? 0) * Math.PI) / 180, cs = Math.cos(th), sn = Math.sin(th);
                    if (th !== 0) {
                        const bw = Math.abs(w0 * cs) + Math.abs(h0 * sn), bh = Math.abs(w0 * sn) + Math.abs(h0 * cs);
                        const cx = (a.rect[0] + a.rect[2]) / 2, cy = (a.rect[1] + a.rect[3]) / 2;
                        rect = [cx - bw / 2, cy - bh / 2, cx + bw / 2, cy + bh / 2];
                    }
                    const tx = (rect[2] - rect[0]) / 2 - ((w0 / 2) * cs - (h0 / 2) * sn), ty = (rect[3] - rect[1]) / 2 - ((w0 / 2) * sn + (h0 / 2) * cs);
                    content = `q ${op < 1 ? "/GS gs " : ""}${num(cs)} ${num(sn)} ${num(-sn)} ${num(cs)} ${num(tx)} ${num(ty)} cm ${num(w0)} 0 0 ${num(h0)} 0 0 cm /Im1 Do Q\n`;
                    extra = `/Name /${a.name.replace(/[^A-Za-z0-9]/g, "") || "Image"} ${op < 1 ? `/CA ${num(op)} ` : ""}`;
                    break;
                }
                default: {
                    subtype = a.kind === "highlight" ? "Highlight" : a.kind === "underline" ? "Underline" : "StrikeOut";
                    const xs = a.quads.flatMap((q) => [q[0]!, q[2]!, q[4]!, q[6]!]), ys = a.quads.flatMap((q) => [q[1]!, q[3]!, q[5]!, q[7]!]);
                    rect = [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
                    resources = "/ExtGState << /GS << /Type /ExtGState /BM /Multiply >> >>";
                    content = "/GS gs\n";
                    for (const q of a.quads) {
                        const p = q.map((v, i) => (i % 2 ? v - rect[1] : v - rect[0]));
                        if (a.kind === "highlight") content += `${rg(a.stroke, false)} ${num(p[0]!)} ${num(p[1]!)} m ${num(p[2]!)} ${num(p[3]!)} l ${num(p[6]!)} ${num(p[7]!)} l ${num(p[4]!)} ${num(p[5]!)} l h f\n`;
                        else {
                            const t = Math.abs(p[1]! - p[5]!);
                            const y = a.kind === "underline" ? Math.min(p[5]!, p[7]!) + t * 0.12 : (p[1]! + p[5]!) / 2;
                            content += `${rg(a.stroke, true)} ${num(Math.max(0.6, t * 0.07))} w ${num(p[4]!)} ${num(y)} m ${num(p[6]!)} ${num(y)} l S\n`;
                        }
                    }
                    extra = `/QuadPoints [${a.quads.map((q) => q.map(num).join(" ")).join(" ")}] /C [${rgb(a.stroke).map(num).join(" ")}] `;
                }
            }
            const [bx0, by0, bx1, by1] = rect;
            await writeStream(apNum, `/Type /XObject /Subtype /Form /BBox [0 0 ${num(bx1 - bx0)} ${num(by1 - by0)}] /Resources << ${resources} >>`, enc.encode(content));
            writeObj(annotNum, `<< ${common}/Subtype /${subtype} /Rect [${[bx0, by0, bx1, by1].map(num).join(" ")}] ${extra}/AP << /N ${apNum} 0 R >> >>`);
            newRefs.push(new PRef(annotNum, 0));
        }
        // recognised text: an invisible-text content stream appended to the page, with a font for it
        const ocr = list.filter((a): a is Extract<UserAnnot, { kind: "ocr" }> => a.kind === "ocr");
        let ocrContent = 0;
        if (ocr.length) {
            const mb = page.cropBox;
            let c = "BT\n3 Tr\n";
            for (const o of ocr) {
                for (const w of o.words) {
                    const [x0, y0, x1, y1] = w.box;
                    const h = Math.max(1, y1 - y0), width = Math.max(1, x1 - x0);
                    const size = Math.max(2, h * 0.85);
                    const natural = Math.max(1, textWidthEstimate(w.t, size));
                    c += `/FgOcr ${num(size)} Tf ${num((width / natural) * 100)} Tz 1 0 0 1 ${num(Math.max(mb[0], x0))} ${num(y0 + h * 0.2)} Tm ${pdfString(w.t)} Tj\n`;
                }
            }
            c += "ET\n";
            ocrContent = newRef();
            await writeStream(ocrContent, "", enc.encode(c));
        }
        // the page: its /Annots gains the new references (and its contents the recognised text)
        const annotsRaw = page.dict.raw("Annots");
        const refList = newRefs.map((r) => `${r.num} 0 R`).join(" ");
        if (annotsRaw instanceof PRef && newRefs.length) {
            const arr = xref.fetch(annotsRaw);
            const existing = Array.isArray(arr) ? arr.map((x) => serialize(x)).join(" ") : "";
            writeObj(annotsRaw.num, `[${existing} ${refList}]`, annotsRaw.gen);
        }
        if (!(annotsRaw instanceof PRef && !ocrContent)) {
            const d = new Dict(null);
            for (const k of page.dict.keys()) if (k !== "Annots" && !(ocrContent && (k === "Contents" || k === "Resources"))) d.set(k, page.dict.raw(k)!);
            let tail = "";
            if (annotsRaw instanceof PRef) tail += `/Annots ${annotsRaw.num} ${annotsRaw.gen} R `;
            else if (newRefs.length || annotsRaw !== undefined) tail += `/Annots [${Array.isArray(annotsRaw) ? annotsRaw.map((x) => serialize(x)).join(" ") : ""} ${refList}] `;
            if (ocrContent) {
                const cur = page.dict.raw("Contents");
                const items = Array.isArray(cur) ? cur.map((x) => serialize(x)) : cur !== undefined && cur !== null ? [serialize(cur)] : [];
                tail += `/Contents [${items.join(" ")} ${ocrContent} 0 R] `;
                const res = page.resources;
                const fonts = new Dict(null);
                const oldFonts = res.dict("Font");
                if (oldFonts) for (const k of oldFonts.keys()) fonts.set(k, oldFonts.raw(k)!);
                const f = new Dict(null);
                f.set("Type", new PName("Font")); f.set("Subtype", new PName("Type1")); f.set("BaseFont", new PName("Helvetica")); f.set("Encoding", new PName("WinAnsiEncoding"));
                fonts.set("FgOcr", f);
                const r2 = new Dict(null);
                for (const k of res.keys()) if (k !== "Font") r2.set(k, res.raw(k)!);
                r2.set("Font", fonts);
                tail += `/Resources ${serialize(r2)} `;
            }
            writeObj(pref.num, serialize(d).slice(0, -2) + tail + ">>", pref.gen);
        }
    }

    if (topFields.length) {
        const afRaw = doc.catalog.raw("AcroForm");
        const af = doc.catalog.dict("AcroForm");
        const keep = new Dict(xref);
        if (af) for (const k of af.keys()) if (k !== "Fields" && k !== "DA" && k !== "DR") keep.set(k, af.raw(k)!);
        const existing = (af?.arr("Fields") ?? []).map((x) => serialize(x)).join(" ");
        const dr = af?.has("DR") ? `/DR ${serialize(af.raw("DR"))} ` : "/DR << /Font << /Helv << /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >> >> >> ";
        const da = af?.str("DA") ? `/DA ${serialize(af.str("DA")!)} ` : "/DA (/Helv 0 Tf 0 g) ";
        const body = serialize(keep).slice(0, -2) + `/Fields [${existing} ${topFields.map((r) => `${r.num} 0 R`).join(" ")}] ${da}${dr}>>`;
        if (afRaw instanceof PRef) writeObj(afRaw.num, body, afRaw.gen);
        else {
            const cat = new Dict(xref);
            for (const k of doc.catalog.keys()) if (k !== "AcroForm") cat.set(k, doc.catalog.raw(k)!);
            writeObj(root.num, serialize(cat).slice(0, -2) + `/AcroForm ${body} >>`, root.gen);
        }
    }
    if (fieldEdits?.size) writeFieldEdits(doc, fieldEdits, { writeObj: (n, b) => writeObj(n, b) });
    if (forms?.size) await writeFormValues(doc, forms, { newRef, writeObj: (n, b) => writeObj(n, b), writeStream: (n, d, data, c) => writeStream(n, d, data, c) });

    writeXref(out, offsets, nextNum, original, xref, root);
    void Stream;
    return out.bytes();
}

/** A rough Helvetica width of `s` at `size`: enough to stretch recognised words to the box they were found in. */
function textWidthEstimate(s: string, size: number): number {
    let w = 0;
    for (const ch of s) w += /[iljtfI.,:;'|!]/.test(ch) ? 0.28 : /[mwMW@]/.test(ch) ? 0.85 : /[A-Z0-9]/.test(ch) ? 0.66 : 0.52;
    return w * size;
}

/** PNG / other image bytes → RGB and alpha planes at (w, h) through the browser. */
async function rasterize(bytes: Uint8Array, mime: string, w: number, h: number): Promise<{ rgb: Uint8Array; alpha: Uint8Array | null }> {
    const bm = await createImageBitmap(new Blob([bytes as BlobPart], { type: mime }));
    const cv = typeof OffscreenCanvas !== "undefined" ? new OffscreenCanvas(w, h) : Object.assign(document.createElement("canvas"), { width: w, height: h });
    const ctx = cv.getContext("2d") as CanvasRenderingContext2D;
    ctx.drawImage(bm, 0, 0, w, h);
    const px = ctx.getImageData(0, 0, w, h).data;
    const rgbOut = new Uint8Array(w * h * 3);
    const a = new Uint8Array(w * h);
    let hasAlpha = false;
    for (let i = 0; i < w * h; i++) { rgbOut[i * 3] = px[i * 4]!; rgbOut[i * 3 + 1] = px[i * 4 + 1]!; rgbOut[i * 3 + 2] = px[i * 4 + 2]!; a[i] = px[i * 4 + 3]!; if (a[i] !== 255) hasAlpha = true; }
    return { rgb: rgbOut, alpha: hasAlpha ? a : null };
}

/** One page of the rearranged document: an existing page (by index) or a new blank one. */
export interface PageSlot {
    /** Index of the page in the current document; null for a new blank page. */
    src: number | null;
    /** Degrees to turn the page clockwise, on top of how it is now (multiples of 90). */
    rotate?: number;
    /** Size in points of a blank page. */
    size?: [number, number];
}

/** Writes the document with its pages in the order of `layout`: pages dropped, turned, moved, or added blank. An incremental update. */
export async function rearrangePages(original: Uint8Array, layout: PageSlot[]): Promise<Uint8Array> {
    if (!layout.length) throw new Error("A document needs at least one page.");
    const doc = PdfDocument.open(original);
    if (doc.xref.trailer.has("Encrypt")) throw new Error("This PDF is encrypted, so its pages can't be changed.");
    const xref = doc.xref;
    const pages = doc.pages();
    const root = xref.trailer.raw("Root");
    if (!(root instanceof PRef)) throw new Error("The PDF has no catalog reference.");
    const catalog = xref.fetch(root);
    const pagesRef = catalog instanceof Dict ? catalog.raw("Pages") : null;
    if (!(pagesRef instanceof PRef)) throw new Error("The PDF has no page tree.");
    const tree = xref.fetch(pagesRef);
    if (!(tree instanceof Dict)) throw new Error("The page tree is damaged.");
    let nextNum = Math.max(xref.trailer.num("Size"), ...[...xref.entries.keys()].map((k) => k + 1));
    const out = new Out();
    out.push(original);
    if (original[original.length - 1] !== 10) out.text("\n");
    const offsets: Array<[number, number]> = [];
    const writeObj = (n: number, body: string, gen = 0) => { offsets.push([n, out.length]); out.text(`${n} ${gen} obj\n${body}\nendobj\n`); };
    const first = pages[0];
    const box = first ? first.mediaBox : ([0, 0, 612, 792] as const);
    const kids: string[] = [];
    for (const slot of layout) {
        if (slot.src === null) {
            const n = nextNum++;
            const [w, h] = slot.size ?? [box[2] - box[0], box[3] - box[1]];
            writeObj(n, `<< /Type /Page /Parent ${pagesRef.num} ${pagesRef.gen} R /MediaBox [0 0 ${num(w)} ${num(h)}] /Resources << >>${slot.rotate ? ` /Rotate ${((slot.rotate % 360) + 360) % 360}` : ""} >>`);
            kids.push(`${n} 0 R`);
            continue;
        }
        const page = pages[slot.src];
        const ref = page?.ref;
        if (!page || !ref) throw new Error(`Page ${slot.src + 1} is not in the document.`);
        if (slot.rotate && slot.rotate % 360 !== 0) {
            const d = new Dict();
            for (const k of page.dict.keys()) if (k !== "Rotate") d.set(k, page.dict.raw(k)!);
            writeObj(ref.num, serialize(d).slice(0, -2) + `/Rotate ${(((page.rotate + slot.rotate) % 360) + 360) % 360} >>`, ref.gen);
        }
        kids.push(`${ref.num} ${ref.gen} R`);
    }
    // The root of the page tree lists every page directly; the pages keep their old parents, which still carry what they inherit.
    const t = new Dict();
    for (const k of tree.keys()) if (k !== "Kids" && k !== "Count") t.set(k, tree.raw(k)!);
    writeObj(pagesRef.num, serialize(t).slice(0, -2) + `/Kids [${kids.join(" ")}] /Count ${kids.length} >>`, pagesRef.gen);
    writeXref(out, offsets, nextNum, original, xref, root);
    return out.bytes();
}
